import { useEffect, useMemo, useState } from 'react';
import { Alert, Box } from '@mui/material';
import { Save } from '@mui/icons-material';

import {
  CustomAutoComplete,
  CustomButton,
  CustomTextField,
  DrawerForm,
  DrawerFormActionSummary,
  DrawerFormActions,
  DrawerFormSection,
} from '../../../../common/components';
import { useCreateSupplierAccountAdjustment } from '../../../../hooks/useSupplierLedger';
import { CURRENCY_OPTIONS } from '../../constants/supplierLedgerConstants';
import { ADJUSTMENT_TYPE_OPTIONS } from '../../constants/supplierLedgerUiConstants';
import { emptyAdjustmentForm } from '../../utils/supplierLedgerHelpers';
import { formatCurrencyAmount, parseNumber } from '../../utils/supplierLedgerFormatters';
import { FieldSelect, invoiceFieldSpan } from '../SupplierLedgerShared';

const AdjustmentDrawer = ({ open, onClose, providers }) => {
  const [form, setForm] = useState(emptyAdjustmentForm);
  const [error, setError] = useState('');
  const createAdjustment = useCreateSupplierAccountAdjustment();
  const providerOptions = useMemo(
    () => providers.map((provider) => ({
      value: provider.uuid,
      label: provider.legal_business_name || provider.company || provider.cuit || 'Proveedor sin nombre',
    })),
    [providers],
  );

  useEffect(() => {
    if (!open) return;
    setForm(emptyAdjustmentForm());
    setError('');
  }, [open]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setError('');
    if (!form.uuid_legal_entity || !form.concept.trim() || !form.amount) {
      setError('Completá proveedor, concepto e importe.');
      return;
    }
    try {
      await createAdjustment.mutateAsync({
        uuid_legal_entity: form.uuid_legal_entity,
        adjustment_type: form.adjustment_type,
        adjustment_date: form.adjustment_date,
        concept: form.concept.trim(),
        currency: form.currency,
        exchange_rate_to_ars: form.currency === 'USD' ? parseNumber(form.exchange_rate_to_ars) : null,
        amount: parseNumber(form.amount),
      });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message);
    }
  };

  return (
    <DrawerForm
      open={open}
      onClose={onClose}
      title="Nuevo ajuste de cuenta corriente"
      width={{ xs: '100vw', sm: 720 }}
      footer={(
        <DrawerFormActions
          summary={<DrawerFormActionSummary label="Importe" value={formatCurrencyAmount(parseNumber(form.amount), form.currency)} />}
        >
          <CustomButton variant="outlined" onClick={onClose}>Cancelar</CustomButton>
          <CustomButton variant="base" startIcon={<Save />} onClick={handleSubmit} isLoading={createAdjustment.isPending}>
            Guardar ajuste
          </CustomButton>
        </DrawerFormActions>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {error && <Box sx={{ mx: 2 }}><Alert severity="error">{error}</Alert></Box>}
        <DrawerFormSection title="Datos del ajuste">
          <CustomAutoComplete
            label="Proveedor"
            name="uuid_legal_entity"
            value={form.uuid_legal_entity}
            onChange={handleChange}
            options={providerOptions}
            inputPlaceholder="Buscar proveedor"
            sx={invoiceFieldSpan(12, 2)}
          />
          <FieldSelect
            label="Tipo"
            name="adjustment_type"
            value={form.adjustment_type}
            onChange={handleChange}
            options={ADJUSTMENT_TYPE_OPTIONS}
            sx={invoiceFieldSpan(6)}
          />
          <CustomTextField
            label="Fecha"
            type="date"
            name="adjustment_date"
            value={form.adjustment_date}
            onChange={handleChange}
            shrinkLabel
            sx={invoiceFieldSpan(6)}
          />
          <CustomTextField
            label="Concepto"
            name="concept"
            value={form.concept}
            onChange={handleChange}
            sx={invoiceFieldSpan(12, 2)}
          />
          <FieldSelect
            label="Moneda"
            name="currency"
            value={form.currency}
            onChange={handleChange}
            options={CURRENCY_OPTIONS}
            sx={invoiceFieldSpan(4)}
          />
          {form.currency === 'USD' && (
            <CustomTextField
              label="Cotización ARS"
              name="exchange_rate_to_ars"
              value={form.exchange_rate_to_ars}
              onChange={handleChange}
              sx={invoiceFieldSpan(4)}
            />
          )}
          <CustomTextField
            label="Importe"
            name="amount"
            value={form.amount}
            onChange={handleChange}
            sx={invoiceFieldSpan(form.currency === 'USD' ? 4 : 8)}
          />
        </DrawerFormSection>
      </Box>
    </DrawerForm>
  );
};

export default AdjustmentDrawer;
